import {
  ConfirmationBase,
  ConfirmationBasePatchRequest,
} from "../types/confirmation";
import { DatePickerField } from "@/shared/components/field/date-picker-field";
import { InputField } from "@/shared/components/field/input-field";
import t from "@/lang/lang";
import { FieldSeparator } from "@/shared/components/ui/field";
import { SourcesParams } from "../../cpy/api/cpy";
import { CpyName } from "../../cpy/types/entity-names";
import { RFC3339timestamp, UUID } from "@bitory-gmbh/nidavellir";
import { CpySelectField } from "@/shared/components/field/cpy-select-field";

/**
 * ConfirmationBaseForm edits the fields every confirmation has. Its fields
 * show and reset the errors of the nearest ErrValidationFailedContextProvider
 * under the keys of the confirmation, e.g. "orderReference".
 */
export function ConfirmationBaseForm({
  confirmationId,
  base,
  onBasePatch,
  cpyDestinationEntityName,
  cpyOfferSourceEntityName,
  cpyOrderSourceEntityName,
  cpyOrderSourcesParams,
  cpyOfferSourcesParams,
  disabled,
}: {
  confirmationId: UUID;
  base: ConfirmationBase;
  onBasePatch: (c: ConfirmationBasePatchRequest) => void;
  cpyDestinationEntityName: CpyName;
  cpyOrderSourceEntityName: CpyName;
  cpyOfferSourceEntityName: CpyName;
  cpyOrderSourcesParams: SourcesParams;
  cpyOfferSourcesParams: SourcesParams;
  disabled?: boolean;
}) {
  return (
    <div className="grid gap-4 lg:grid-cols-3">
      <DatePickerField
        label={t("Datum Auftragsbestätigung")}
        nullable={false}
        date={base.date}
        onDateChange={(date: RFC3339timestamp) => onBasePatch({ date })}
        fieldName="date"
        disabled={disabled}
      />
      <FieldSeparator className="col-span-full" />
      <CpySelectField
        label={t("Auftragseingang")}
        description={t(
          "Die aktuellen Daten werden durch die Auswahl überschrieben.",
        )}
        warning=""
        value={base.orderId}
        onValueReset={() => onBasePatch({ orderId: null })}
        destinationId={confirmationId}
        destinationEntityName={cpyDestinationEntityName}
        sourceEntityName={cpyOrderSourceEntityName}
        sourcesParams={cpyOrderSourcesParams}
        fieldName="orderId"
        disabled={disabled}
      />
      <InputField
        label={t("Referenz Auftragseingang")}
        description={t("z.B. Kundenreferenz auf Bestellung")}
        value={base.orderReference}
        onChange={(e) => onBasePatch({ orderReference: e.target.value })}
        fieldName="orderReference"
        disabled={disabled}
      />
      <DatePickerField
        nullable
        date={base.orderDate}
        onDateChange={(orderDate) =>
          onBasePatch({ orderDate: orderDate ?? null })
        }
        label={t("Datum Auftragseingang")}
        fieldName="orderDate"
        disabled={disabled}
      />
      <FieldSeparator className="col-span-full" />
      <CpySelectField
        label={t("Angebotseingang")}
        description={t(
          "Die aktuellen Daten werden durch die Auswahl überschrieben.",
        )}
        warning=""
        value={base.offerId}
        onValueReset={() => onBasePatch({ offerId: null })}
        destinationId={confirmationId}
        destinationEntityName={cpyDestinationEntityName}
        sourceEntityName={cpyOfferSourceEntityName}
        sourcesParams={cpyOfferSourcesParams}
        fieldName="offerId"
        disabled={disabled}
      />
      <InputField
        label={t("Referenz Angebot")}
        description={t("z.B. Angebotsnummer")}
        value={base.offerReference}
        onChange={(e) => onBasePatch({ offerReference: e.target.value })}
        fieldName="offerReference"
        disabled={disabled}
      />
      <DatePickerField
        nullable
        date={base.offerDate}
        onDateChange={(offerDate) =>
          onBasePatch({ offerDate: offerDate ?? null })
        }
        label={t("Datum Angebot")}
        fieldName="offerDate"
        disabled={disabled}
      />
    </div>
  );
}
